/**
 * La matematica della lente, senza DOM.
 *
 * La lente è il pinch «a molla»: ingrandisce finché le due dita restano sullo
 * schermo, e al rilascio la vista torna da sola al punto di partenza. Qui c'è
 * solo il calcolo — quante volte ingrandire, attorno a quale punto, di quanto
 * spostare — così le regole che contano si possono verificare senza montare
 * niente e senza simulare un tocco.
 *
 * Vedi `hooks/useLente.ts` per il gesto e `hooks/useBloccoLente.ts` per le
 * schermate che la spengono.
 */

export interface Punto {
  x: number;
  y: number;
}

/**
 * Il tetto dell'ingrandimento. Tre volte è già molto: il pinch serve a leggere
 * una casella o un'etichetta piccola, non a navigare una mappa, e più si sale
 * più il contenuto sotto le dita diventa poco (a 3× della viewport a 360px si
 * guardano 120px di larghezza).
 */
export const SCALA_MASSIMA = 3;

/**
 * Il pavimento è 1, e non è una scelta estetica: è metà del difetto da
 * riparare. Con un minimo sotto l'unità il pinch verso l'interno rimpicciolisce
 * la pagina — e una pagina rimpicciolita, in un'app a schermo pieno, è
 * esattamente la vista rotta da cui l'utente non sapeva più uscire.
 */
export const SCALA_MINIMA = 1;

/** Distanza euclidea fra due dita. */
export function distanza(a: Punto, b: Punto): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/** Il punto attorno a cui si ingrandisce: il centro fra le due dita. */
export function puntoMedio(a: Punto, b: Punto): Punto {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Quante volte ingrandire, dato quanto si sono allontanate le dita rispetto a
 * dove erano quando il gesto è cominciato.
 *
 * `distanzaIniziale` a zero (due dita nello stesso punto, o un evento sintetico
 * malfatto) restituisce 1 invece di `Infinity`: una divisione per zero qui
 * diventerebbe una `transform` con `NaN`, che il browser scarta in silenzio
 * lasciando la pagina a metà gesto.
 */
export function scalaLimitata(distanzaCorrente: number, distanzaIniziale: number): number {
  if (!(distanzaIniziale > 0) || !Number.isFinite(distanzaCorrente)) return SCALA_MINIMA;
  const grezza = distanzaCorrente / distanzaIniziale;
  return Math.min(SCALA_MASSIMA, Math.max(SCALA_MINIMA, grezza));
}

export interface Trasformazione {
  scala: number;
  dx: number;
  dy: number;
}

/**
 * La stringa da mettere in `style.transform`.
 *
 * L'ordine conta: `translate` PRIMA di `scale`. Al contrario lo spostamento
 * verrebbe moltiplicato per la scala, e le dita si scollerebbero dal contenuto
 * che stanno trascinando — più si ingrandisce, più la pagina scapperebbe.
 */
export function trasformazione({ scala, dx, dy }: Trasformazione): string {
  return `translate(${dx}px, ${dy}px) scale(${scala})`;
}

/**
 * Lo spostamento del centro del gesto: la lente segue le dita, così si può
 * spostare la parte ingrandita senza rilasciare (e quindi senza che la molla
 * riporti tutto indietro).
 */
export function spostamento(centroIniziale: Punto, centroCorrente: Punto): { dx: number; dy: number } {
  return { dx: centroCorrente.x - centroIniziale.x, dy: centroCorrente.y - centroIniziale.y };
}

/**
 * Il gesto conta come pinch solo dopo che le dita si sono mosse di qualcosa.
 * Sotto questa soglia si resta fermi a scala 1: due dita appoggiate insieme —
 * un pollice di troppo mentre si tocca un bottone — non devono far tremolare la
 * pagina.
 */
export const SOGLIA_PINCH_PX = 12;

/** Il gesto è cominciato davvero? */
export function pinchAvviato(distanzaCorrente: number, distanzaIniziale: number): boolean {
  return Math.abs(distanzaCorrente - distanzaIniziale) >= SOGLIA_PINCH_PX;
}
